import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminLegalEntityService } from '@/shared/api'
import type { LegalEntityListRequest } from '@/shared/api'

const QUERY_KEYS = {
  list: (params?: LegalEntityListRequest) => ['legal-entities', 'list', params],
  details: (id: number) => ['legal-entities', 'details', id],
}

export function useLegalEntities(
  params?: LegalEntityListRequest,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: QUERY_KEYS.list(params),
    queryFn: () => AdminLegalEntityService.getLegalEntities(params),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useLegalEntityDetails(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.details(id),
    queryFn: () => AdminLegalEntityService.getLegalEntityDetails(id),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false && id > 0,
  })
}

export function useApproveLegalEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => AdminLegalEntityService.approveLegalEntity(id),
    onSuccess: (response) => {
      toast.success(response.message || 'Юридическое лицо одобрено')
      queryClient.invalidateQueries({ queryKey: ['legal-entities'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка одобрения')
      }
    },
  })
}

export function useRejectLegalEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      AdminLegalEntityService.rejectLegalEntity(id, reason),
    onSuccess: (response) => {
      toast.success(response.message || 'Заявка отклонена')
      queryClient.invalidateQueries({ queryKey: ['legal-entities'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка отклонения')
      }
    },
  })
}

export function useDisableLegalEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      AdminLegalEntityService.disableLegalEntity(id, reason),
    onSuccess: (response) => {
      toast.success(response.message || 'Личный кабинет отключён')
      queryClient.invalidateQueries({ queryKey: ['legal-entities'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка отключения')
      }
    },
  })
}

export function useEnableLegalEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => AdminLegalEntityService.enableLegalEntity(id),
    onSuccess: (response) => {
      toast.success(response.message || 'Личный кабинет включён')
      queryClient.invalidateQueries({ queryKey: ['legal-entities'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка включения')
      }
    },
  })
}
